/**
 * Utilities to detect in-app browsers (Instagram/Facebook) and try to open links externally.
 */

export function isInstagramInApp(ua?: string): boolean {
  const u = (
    ua || (typeof navigator !== "undefined" ? navigator.userAgent : "")
  ).toLowerCase();
  return u.includes("instagram");
}

export function isFacebookInApp(ua?: string): boolean {
  const u = (
    ua || (typeof navigator !== "undefined" ? navigator.userAgent : "")
  ).toLowerCase();
  // FBAN/FBAV are common markers for Facebook app in-app browser
  return u.includes("fban") || u.includes("fbav") || u.includes("facebook");
}

export function isInAppBrowser(ua?: string): boolean {
  return isInstagramInApp(ua) || isFacebookInApp(ua);
}

export function isInstagramIOSInApp(ua?: string): boolean {
  return isInstagramInApp(ua) && isIOS(ua);
}

export function isAndroid(ua?: string): boolean {
  const u = (
    ua || (typeof navigator !== "undefined" ? navigator.userAgent : "")
  ).toLowerCase();
  return u.includes("android");
}

export function isIOS(ua?: string): boolean {
  const u = (
    ua || (typeof navigator !== "undefined" ? navigator.userAgent : "")
  ).toLowerCase();
  return /iphone|ipad|ipod/.test(u);
}

/**
 * Try to open the given absolute URL in an external browser when possible.
 * On Android inside FB/IG in-app, we use intent:// to target Chrome with a fallback.
 * On iOS, programmatic external open is not reliable; we fall back to target _blank
 * and let the caller show instructions to open in Safari.
 */
export function attemptOpenExternal(url: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const isInApp = isInAppBrowser();
    if (!isInApp) {
      // Normal browser, just navigate
      window.location.href = url;
      return true;
    }

    if (isAndroid()) {
      // Build intent:// URL for Chrome on Android
      const target = new URL(url);
      const hostAndPath = `${target.host}${target.pathname}${target.search}`;
      const fallback = encodeURIComponent(url);
      const intentUrl = `intent://${hostAndPath}#Intent;scheme=${target.protocol.replace(
        ":",
        ""
      )};action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;S.browser_fallback_url=${fallback};end`;
      // Schedule a fallback to the normal URL in case the intent is blocked by the in-app webview
      window.setTimeout(() => {
        try {
          // If intent didn't work, at least navigate inside the webview
          window.location.href = url;
        } catch {}
      }, 1200);
      // Use a real anchor click; some webviews block location changes to intent:// but allow user-gesture anchor
      const a = document.createElement("a");
      a.href = intentUrl;
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      try {
        a.click();
      } finally {
        // Clean up shortly after click
        window.setTimeout(() => {
          try {
            document.body.removeChild(a);
          } catch {}
        }, 500);
      }
      return true;
    }

    // iOS (Instagram/Facebook): cannot force Safari programmatically.
    // Best-effort: try opening other browsers via URL schemes, otherwise return false so caller navigates in-webview.
    // Note: window.open returns null/undefined when blocked, so it's a reasonable success heuristic.
    const tryOpen = (u: string) => {
      try {
        const w = window.open(u, "_blank");
        return !!w;
      } catch {
        return false;
      }
    };

    const target = new URL(url);
    const isHttps = target.protocol === "https:";
    const full = target.toString();

    // Chrome on iOS
    const chromeScheme = `${isHttps ? "googlechromes" : "googlechrome"}://${
      target.host
    }${target.pathname}${target.search}`;
    if (tryOpen(chromeScheme)) return true;

    // Firefox on iOS
    const firefoxScheme = `firefox://open-url?url=${encodeURIComponent(full)}`;
    if (tryOpen(firefoxScheme)) return true;

    // Brave on iOS
    const braveScheme = `brave://open-url?url=${encodeURIComponent(full)}`;
    if (tryOpen(braveScheme)) return true;

    // Edge on iOS
    const edgeScheme = `microsoft-edge-${isHttps ? "https" : "http"}://${
      target.host
    }${target.pathname}${target.search}`;
    if (tryOpen(edgeScheme)) return true;

    // As a last tiny nudge, attempting top-level navigation (may be blocked by sandboxing)
    try {

      if (window.top && window.top !== window) {
        window.top.location.href = full;
        return true;
      }
    } catch {}
    // If all attempts fail, signal to the caller to handle a fallback (e.g., navigate in-webview)
    return false;
  } catch {
    return false;
  }
}
